import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import SEO, { articleSchema } from "../components/SEO";
import {
  doc, getDoc, collection, getDocs,
  query, limit, orderBy, where,
  updateDoc, increment, addDoc, serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/firebase-config";
import Navbar from "./Navbar";
import Footer from "./Footer";
import "../Styles/ContractTips.css";
import { sanitizeHtml } from "../utils/sanitizeHtml";
import VoiceReader from "../components/VoiceReader";
import ReadingMomentum from "../components/ReadingMomentum";

/* ── PURE UTILS ─────────────────────────────────────────── */

const parseContent = (html = "") => {
  const text  = html.replace(/<[^>]+>/g, " ").trim();
  const words = text.split(/\s+/).filter(Boolean);
  return { wordCount: words.length, readMins: Math.max(1, Math.ceil(words.length / 200)) };
};

/* Regex-based heading extractor — no DOM needed */
const extractHeadings = (html = "") => {
  const re = /<h([23])[^>]*>([\s\S]*?)<\/h\1>/gi;
  const out = [];
  let m, i = 0;
  while ((m = re.exec(html)) !== null) {
    out.push({ id: `h-${i++}`, level: `h${m[1]}`, text: m[2].replace(/<[^>]+>/g, "").trim() });
  }
  return out;
};

/* Inject stable IDs into h2/h3 tags so TOC anchors work */
const patchHeadingIds = (html = "") => {
  let i = 0;
  return html.replace(/<(h[23])([^>]*?)>/gi, (_, tag, attrs) => {
    const clean = attrs.replace(/\s*id="[^"]*"/gi, "");
    return `<${tag}${clean} id="h-${i++}">`;
  });
};

/* Handle Firestore Timestamp or ISO string */
const formatDate = (val) => {
  if (!val) return null;
  const d = val?.toDate ? val.toDate() : new Date(val);
  if (isNaN(d)) return null;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};

/* ── READING PROGRESS BAR ───────────────────────────────── */
const ReadingProgress = () => {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const el    = document.documentElement;
      const total = Math.max(0, el.scrollHeight - window.innerHeight);
      setPct(total > 0 ? Math.min(100, (window.scrollY / total) * 100) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);
  return (
    <div className="read-progress-track" aria-hidden="true">
      <div className="read-progress-fill" style={{ width: `${pct}%` }} />
    </div>
  );
};

/* ── TABLE OF CONTENTS ──────────────────────────────────── */
const TableOfContents = ({ headings, activeId }) => (
  <aside className="td-toc" aria-label="Table of contents">
    <p className="td-toc-label">In this tip</p>
    <nav>
      {headings.map(h => (
        <a
          key={h.id}
          href={`#${h.id}`}
          className={`td-toc-item ${h.level}${activeId === h.id ? " active" : ""}`}
          onClick={e => {
            e.preventDefault();
            document.getElementById(h.id)?.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
        >
          {h.text}
        </a>
      ))}
    </nav>
  </aside>
);

const fmtCommentDate = val => {
  if (!val) return '';
  const d = val?.toDate ? val.toDate() : new Date(val);
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};

/* ── LIKE BUTTON ── */
const LikeButton = ({ slug, initialCount }) => {
  const likedKey = `pp_liked_tip_${slug}`;
  const [liked, setLiked]   = useState(() => !!localStorage.getItem(likedKey));
  const [count, setCount]   = useState(initialCount || 0);
  const [bounce, setBounce] = useState(false);

  const handleLike = () => {
    if (liked) return;
    setLiked(true);
    setCount(c => c + 1);
    setBounce(true);
    setTimeout(() => setBounce(false), 600);
    localStorage.setItem(likedKey, '1');
    updateDoc(doc(db, 'contract_tips', slug), { likes: increment(1) }).catch(() => {});
  };

  return (
    <button
      className={`blog-like-btn${liked ? ' liked' : ''}${bounce ? ' bounce' : ''}`}
      onClick={handleLike}
      aria-label={liked ? 'Liked' : 'Like this tip'}
      title={liked ? 'You liked this!' : 'Like this tip'}
    >
      <span className="blog-like-heart">{liked ? '♥' : '♡'}</span>
      <span className="blog-like-count">{count}</span>
      <span className="blog-like-label">{liked ? 'Liked!' : 'Like'}</span>
    </button>
  );
};

/* ── COMMENTS SECTION ── */
const CommentsSection = ({ slug }) => {
  const [comments, setComments] = useState([]);
  const [loadingC, setLoadingC] = useState(true);
  const [form, setForm]         = useState({ name: '', text: '' });
  const [honeypot, setHoneypot] = useState('');
  const [status, setStatus]     = useState('idle');
  const loadTimeRef             = useRef(Date.now());

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDocs(
          query(collection(db, 'contract_tips', slug, 'comments'), orderBy('createdAt', 'asc'))
        );
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setComments(all.filter(c => c.approved === true));
      } catch {
        setComments([]);
      } finally {
        setLoadingC(false);
      }
    };
    load();
  }, [slug]);

  const handleSubmit = async e => {
    e.preventDefault();
    if (honeypot) return;
    if (Date.now() - loadTimeRef.current < 3000) return;
    const { name, text } = form;
    if (!name.trim() || !text.trim()) return;
    setStatus('sending');
    try {
      await addDoc(collection(db, 'contract_tips', slug, 'comments'), {
        name: name.trim(),
        text: text.trim(),
        approved: false,
        createdAt: serverTimestamp(),
      });
      setStatus('success');
      setForm({ name: '', text: '' });
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="blog-comments">
      <h3 className="blog-comments-heading">
        {comments.length > 0 ? `${comments.length} Comment${comments.length !== 1 ? 's' : ''}` : 'Leave a Comment'}
      </h3>

      {!loadingC && comments.length > 0 && (
        <div className="blog-comments-list">
          {comments.map(c => (
            <div key={c.id} className="blog-comment">
              <div className="blog-comment-avatar">{(c.name || '?')[0].toUpperCase()}</div>
              <div className="blog-comment-body">
                <div className="blog-comment-meta">
                  <strong className="blog-comment-name">{c.name}</strong>
                  <time className="blog-comment-date">{fmtCommentDate(c.createdAt)}</time>
                </div>
                <p className="blog-comment-text">{c.text}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="blog-comment-form-wrap">
        <h4 className="blog-comment-form-title">Share your thoughts</h4>
        <p className="blog-comment-form-note">Comments are reviewed before appearing.</p>

        {status === 'success' ? (
          <div className="blog-comment-success">
            <span>✓</span> Thanks! Your comment is under review and will appear soon.
          </div>
        ) : (
          <form className="blog-comment-form" onSubmit={handleSubmit}>
            <input
              type="text"
              value={honeypot}
              onChange={e => setHoneypot(e.target.value)}
              tabIndex={-1}
              aria-hidden="true"
              style={{ position: 'absolute', left: '-9999px', opacity: 0, width: 1, height: 1 }}
              autoComplete="off"
            />
            <div className="blog-comment-fields">
              <input
                type="text"
                placeholder="Your name *"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="blog-comment-input"
                required
                maxLength={60}
              />
              <textarea
                placeholder="Write your comment…"
                value={form.text}
                onChange={e => setForm(f => ({ ...f, text: e.target.value }))}
                className="blog-comment-textarea"
                required
                rows={4}
                maxLength={1000}
              />
            </div>
            {status === 'error' && (
              <p style={{ color: '#c25b2a', fontSize: '0.85rem', margin: '0.4rem 0' }}>
                Something went wrong. Please try again.
              </p>
            )}
            <button type="submit" className="blog-comment-submit" disabled={status === 'sending'}>
              {status === 'sending' ? 'Submitting…' : 'Post Comment'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

/* ── MAIN COMPONENT ─────────────────────────────────────── */
const ContractTipDetail = () => {
  const { slug } = useParams();
  const [tip, setTip]                   = useState(null);
  const [related, setRelated]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [copied, setCopied]             = useState(false);
  const [activeHeading, setActiveHeading] = useState(null);
  const [lightboxSrc, setLightboxSrc]   = useState(null);
  const contentRef = useRef(null);

  /* ── FETCH ── */
  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchTip = async () => {
      try {
        const docSnap = await getDoc(doc(db, "contract_tips", slug));
        if (!docSnap.exists()) { setLoading(false); return; }

        const data = { id: docSnap.id, ...docSnap.data() };
        setTip(data);

        /* View tracking — once per 24h per browser */
        const viewKey = `pp_viewed_tip_${slug}`;
        const lastViewed = parseInt(localStorage.getItem(viewKey) || '0');
        if (Date.now() - lastViewed > 24 * 60 * 60 * 1000) {
          updateDoc(doc(db, 'contract_tips', slug), { views: increment(1) }).catch(() => {});
          localStorage.setItem(viewKey, String(Date.now()));
        }

        /* Related: same category first, pad with any tips if needed */
        let rel = [];
        if (data.category) {
          try {
            const catSnap = await getDocs(
              query(collection(db, "contract_tips"), where("category", "==", data.category), limit(5))
            );
            rel = catSnap.docs
              .map(d => ({ id: d.id, ...d.data() }))
              .filter(d => d.id !== docSnap.id);
          } catch (_) { /* composite index not yet created — fall through */ }
        }
        if (rel.length < 3) {
          const anySnap = await getDocs(
            query(collection(db, "contract_tips"), orderBy("importance", "asc"), limit(7))
          );
          const pad = anySnap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(d => d.id !== docSnap.id && !rel.find(r => r.id === d.id));
          rel = [...rel, ...pad];
        }
        setRelated(rel.slice(0, 3));
      } catch (err) {
        console.error("ContractTipDetail fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchTip();
  }, [slug]);

  /* ── DERIVED STATE ── */
  const { wordCount, readMins } = useMemo(
    () => (tip ? parseContent(tip.content) : { wordCount: 0, readMins: 0 }),
    [tip]
  );
  const patchedContent = useMemo(() => sanitizeHtml(patchHeadingIds(tip?.content || "")), [tip]);
  const headings       = useMemo(() => extractHeadings(tip?.content || ""), [tip]);
  const formattedDate  = useMemo(
    () => formatDate(tip?.publishedAt || tip?.date || tip?.createdAt),
    [tip]
  );
  const hasToc = headings.length >= 3;

  /* ── ARTICLE MEDIA: image zoom + iframe resize ── */
  useEffect(() => {
    const el = contentRef.current;
    if (!el || !patchedContent) return;

    const onImgClick = e => {
      if (e.target.tagName === 'IMG') setLightboxSrc(e.target.src);
    };
    el.addEventListener('click', onImgClick);

    el.querySelectorAll('iframe').forEach(iframe => {
      if (iframe.parentElement?.classList.contains('article-iframe-wrap')) return;
      const wrap = document.createElement('div');
      wrap.className = 'article-iframe-wrap';
      iframe.parentNode.insertBefore(wrap, iframe);
      wrap.appendChild(iframe);
    });

    return () => el.removeEventListener('click', onImgClick);
  }, [patchedContent]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── ACTIVE HEADING OBSERVER ── */
  useEffect(() => {
    if (!hasToc) return;
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) setActiveHeading(e.target.id); }),
      { rootMargin: "-15% 0% -75% 0%", threshold: 0 }
    );
    headings.forEach(h => {
      const el = document.getElementById(h.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [headings, hasToc]);

  /* ── COPY LINK ── */
  const handleShare = useCallback(() => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    }).catch(() => {});
  }, []);

  /* ── LOADING ── */
  if (loading) {
    return (
      <>
        <Navbar />
        <div className="td-skeleton-page">
          <div className="td-hero-skel" />
          <div className="td-body-skel">
            {[85, 65, 78, 50, 70, 40].map((w, i) => (
              <div key={i} className="skeleton-line" style={{ width: `${w}%`, height: 14, marginBottom: 12 }} />
            ))}
          </div>
        </div>
        <Footer />
      </>
    );
  }

  /* ── NOT FOUND ── */
  if (!tip) {
    return (
      <>
        <Navbar />
        <div className="tips-empty" style={{ paddingTop: "8rem" }}>
          <span className="empty-icon">—</span>
          <h3>Tip not found</h3>
          <p>We couldn't locate a tip for <em>{slug}</em>.</p>
          <Link to="/contract-tips" className="empty-cta">← Back to tips</Link>
        </div>
        <Footer />
      </>
    );
  }

  const coverSrc   = tip.coverImage || tip.thumbnail || null;
  const tipDesc    = tip.summary || tip.excerpt || 'Expert contract tip from Pooja Biswal, contract specialist London.';
  const tipPubDate = tip.publishedAt?.toDate
    ? tip.publishedAt.toDate().toISOString()
    : (tip.createdAt?.toDate ? tip.createdAt.toDate().toISOString() : '');
  const tipKeywords = [
    tip.category,
    ...(tip.tags || []),
    'contract tips UK', 'contract specialist London', 'freelance contracts',
  ].filter(Boolean).join(', ');

  return (
    <>
      <SEO
        title={`${tip.title} — Contract Tips`}
        description={tipDesc}
        keywords={tipKeywords}
        canonical={`/contract-tips/${tip.id}`}
        ogImage={coverSrc || undefined}
        ogType="article"
        schema={articleSchema({
          title: tip.title,
          description: tipDesc,
          slug: tip.id,
          image: coverSrc,
          datePublished: tipPubDate,
          keywords: tipKeywords,
        })}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Contract Tips", path: "/contract-tips" },
          { name: tip.title, path: `/contract-tips/${tip.id}` },
        ]}
      />
      <ReadingProgress />
      <Navbar />

      <article className="td-page">

        {/* ── HERO ── */}
        <header
          className={`td-hero${!coverSrc ? " td-hero--no-cover" : ""}`}
          style={coverSrc ? {
            backgroundImage: `url(${coverSrc})`,
            backgroundPosition: `${tip.coverPositionX ?? 50}% ${tip.coverPositionY ?? 50}%`,
          } : {}}
        >
          {coverSrc && <div className="td-hero-overlay" />}

          <div className="td-hero-content">
            <Link to="/contract-tips" className="td-back-btn">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
              All Tips
            </Link>

            <div className="td-hero-badges">
              {tip.category && <span className="tip-category">{tip.category}</span>}
              <span className="td-read-time">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                </svg>
                {readMins} min read
              </span>
              {wordCount > 0 && (
                <span className="td-word-count">{wordCount.toLocaleString()} words</span>
              )}
              {tip.views > 0 && (
                <span className="td-read-time">{tip.views.toLocaleString()} views</span>
              )}
              {tip.likes > 0 && (
                <span className="td-read-time">♥ {tip.likes}</span>
              )}
            </div>

            <h1 className="td-title">{tip.title}</h1>
            {tip.subtitle && <p className="td-subtitle">{tip.subtitle}</p>}

            <div className="td-hero-meta-row">
              {tip.author && (
                <div className="td-hero-author">
                  <img
                    src={tip.author.avatar}
                    alt={tip.author.name}
                    className="td-author-img"
                    onError={e => { e.target.style.display = "none"; }}
                  />
                  <div>
                    <span className="td-author-name">{tip.author.name}</span>
                    <span className="td-author-role">{tip.author.role}</span>
                  </div>
                </div>
              )}
              {formattedDate && (
                <time className="td-publish-date" dateTime={formattedDate}>{formattedDate}</time>
              )}
            </div>
          </div>
        </header>

        {/* ── ARTICLE BODY ── */}
        <div className={`td-wrapper${hasToc ? " td-wrapper--wide" : ""}`}>
          <div className={`td-layout${hasToc ? " td-layout--has-toc" : ""}`}>

            <div className="td-content-col">

              <VoiceReader title={tip.title} content={tip.content} contentRef={contentRef} audioUrl={tip.audioUrl} audioWords={tip.audioWords} />

              {tip.subtitle && (
                <aside className="td-pull-quote" aria-label="Key takeaway">
                  <span className="pull-quote-mark">"</span>
                  {tip.subtitle}
                </aside>
              )}

              <div
                ref={contentRef}
                className="td-body"
                dangerouslySetInnerHTML={{ __html: patchedContent }}
              />

              {/* ── LIKE + SHARE ── */}
              <div className="blog-engage-row">
                <LikeButton slug={tip.id || slug} initialCount={tip.likes || 0} />
                <button
                  className={`td-share-btn${copied ? " copied" : ""}`}
                  onClick={handleShare}
                  aria-label="Copy link to this tip"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                  </svg>
                  {copied ? "Copied!" : "Copy link"}
                </button>
              </div>

              {/* ── TAGS ── */}
              {tip.tags && tip.tags.length > 0 && (
                <div className="td-tags-row">
                  <span className="td-tags-label">Tagged:</span>
                  {tip.tags.map((tag, i) => (
                    <span key={i} className="tip-tag">#{tag}</span>
                  ))}
                </div>
              )}

              {/* ── REFERENCES — supports string URLs or {url, label} objects ── */}
              {tip.referenceLinks && tip.referenceLinks.length > 0 && (
                <section className="td-references">
                  <h3 className="td-section-label">Further Reading</h3>
                  <ul>
                    {tip.referenceLinks.map((ref, i) => {
                      const url   = typeof ref === "object" ? ref.url   : ref;
                      const label = typeof ref === "object" ? (ref.label || ref.url) : ref;
                      return (
                        <li key={i}>
                          <a href={url} target="_blank" rel="noopener noreferrer">{label}</a>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              )}

              {/* ── AUTHOR CARD ── */}
              {tip.author && (
                <div className="td-author-card">
                  <img
                    src={tip.author.avatar}
                    alt={tip.author.name}
                    className="td-author-card-img"
                    onError={e => { e.target.style.display = "none"; }}
                  />
                  <div className="td-author-card-body">
                    <span className="td-author-card-label">Written by</span>
                    <h4 className="td-author-card-name">{tip.author.name}</h4>
                    <p className="td-author-card-role">{tip.author.role}</p>
                    {tip.author.bio && (
                      <p className="td-author-card-bio">{tip.author.bio}</p>
                    )}
                  </div>
                </div>
              )}

              {/* ── COMMENTS ── */}
              <CommentsSection slug={tip.id || slug} />

              <div className="td-end-cta">
                <Link to="/contract-tips" className="td-back-cta">
                  ← Back to all contract tips
                </Link>
              </div>

            </div>

            {/* ── TABLE OF CONTENTS (sidebar) ── */}
            {hasToc && (
              <TableOfContents headings={headings} activeId={activeHeading} />
            )}

          </div>
        </div>

        {/* ── RELATED TIPS ── */}
        {related.length > 0 && (
          <section className="td-related">
            <div className="td-related-inner">
              <h2 className="td-related-heading">
                <span className="td-related-accent" />
                {tip.category ? `More in ${tip.category}` : "More Tips"}
              </h2>
              <div className="td-related-grid">
                {related.map(r => (
                  <Link key={r.id} to={`/contract-tips/${r.slug || r.id}`} className="td-related-card">
                    {(r.thumbnail || r.coverImage) && (
                      <div
                        className="td-related-img"
                        style={{ backgroundImage: `url(${r.thumbnail || r.coverImage})` }}
                      />
                    )}
                    <div className={`td-related-body${!(r.thumbnail || r.coverImage) ? " no-img" : ""}`}>
                      {r.category && <span className="td-related-cat">{r.category}</span>}
                      <h4 className="td-related-title">{r.title}</h4>
                      {r.summary && <p className="td-related-summary">{r.summary}</p>}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

      </article>

      <Footer />

      {lightboxSrc && (
        <div className="article-lightbox" onClick={() => setLightboxSrc(null)}>
          <img src={lightboxSrc} alt="Enlarged view" onClick={e => e.stopPropagation()} />
          <button className="article-lightbox-close" onClick={() => setLightboxSrc(null)}>✕</button>
        </div>
      )}

      <ReadingMomentum />
    </>
  );
};

export default ContractTipDetail;
